import {AfterViewInit, DestroyRef, Directive, ElementRef, Input} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {standardStreng} from "felles-ng2/støttefunksjoner/strenger";
import {siste} from "felles-ng2/støttefunksjoner/lister";

const debug = initDebugger('ToggleKlasseVedStickyDirektiv');

/**
 * Setter klasse på elementet direktivet anvendes på når dette elementet blir sticky.
 *
 * Forutsetter at elementet er gjort sticky via css, se lederplan-klient/src/styles/web-ng2/mixins/sticky.scss
 * MERK at css prop top eller bottom må være satt til -1px for at direktivet skal fungere korrekt.
 *
 * Benytter DOM apiet IntersectionObserver:
 * The IntersectionObserver interface of the Intersection Observer API provides a way to asynchronously observe changes
 * in the intersection of a target element with an ancestor element or with a top-level document's viewport.
 * The ancestor element or viewport is referred to as the root.
 * Se https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver
 *
 * Bruk:
 * ```
 * <etElement toggleKlasseVedSticky>
 * alternativt kan ønsket klasse som skal legges til elementet ved sticky angis:
 * <etElement toggleKlasseVedSticky="en-css-klasse">
 * ```
 */
@Directive({
    selector: '[toggleKlasseVedSticky]',
    standalone: true
})
export class ToggleKlasseVedStickyDirektiv implements AfterViewInit {
    private static cssKlass = 'er-sticky';

    @Input({alias: 'toggleKlasseVedSticky'}) stickyklasse: string = ''; //dersom ikke angitt via 'toggleKlasseVedSticky="en-css-klasse"' vil stickyklasse få verdien '', uavhengig av hva vi init den til her..

    constructor(private el: ElementRef<HTMLElement>, private destroyRef: DestroyRef) {
    }

    ngAfterViewInit(): void {
        toggleKlasseVedSticky(this.el.nativeElement, standardStreng(this.stickyklasse, ToggleKlasseVedStickyDirektiv.cssKlass), this.destroyRef)
    }
}

export function vedSticky(element: HTMLElement, vedSticky: (erSticky: boolean) => void, destroyRef: DestroyRef) {
    const observatør = new IntersectionObserver(
      observasjoner => {
          /*observasjoner.forEach((o, i) => {
              console.log(`# observasjoner ${i} isIntersecting: ${o.isIntersecting}`);
              console.log(`# observasjoner ${i} intersectionRatio: ${o.intersectionRatio}`);
              console.log(`# observasjoner ${i} intersectionRect: ${JSON.stringify(o.intersectionRect)}`);
              console.log(`# observasjoner ${i} boundingClientRect: ${JSON.stringify(o.boundingClientRect)}`);
              console.log(`# observasjoner ${i} target.outerHTML: ${o.target.outerHTML}`);
          })*/
          //Vi kun ett element (observatør.observe(element)). Callbacken får en liste med innslag, og innslagene er i kronologisk rekkefølge (eldst først).
          //For ett enkelt element vil listen så å si alltid ha lengde 1 — men nettleseren kan i noen tilfeller batche flere tilstandsendringer inn i samme callback hvis det skjedde flere kryssinger mellom to leveringer.
          //Sjekker derfor kun mot siste (seneste i tid) innslag
          const erSticky = !siste(observasjoner)!.isIntersecting;
          vedSticky(erSticky);
      },
      {threshold: [1]}
    );

    //start å observere
    observatør.observe(element);

    //terminer observering når element blir terminert
    destroyRef.onDestroy(() => observatør.disconnect());
}

/** Legger til angitt cssKlasse dersom angitt element ikke er synlig, dvs scrolling er nødvendig for at elementet skal bli synlig.
 * Merk at denne funksjonen ikke må kalles før angitt elements innhold har blitt tegnet ut da beregning av nødvendigheten for
 * scrolling ellers blir feil, ofte med det resultat at cssKlasse legges til selvom ingen scrolling er nødvendig.
 * Som hovedregel bør derfor kall til toggleKlasseVedSticky(..) legges i {@link AfterViewInit} */
export function toggleKlasseVedSticky(element: HTMLElement, cssKlasse: string, destroyRef: DestroyRef) {

    function stickyAktivert(aktivert: boolean) {
        debug('stickyAktivert %s "%s"', aktivert, cssKlasse)
        element.classList.toggle(cssKlasse, aktivert);
    }

    vedSticky(element, stickyAktivert, destroyRef);
}
